<script setup lang="ts">
	import { ref, onMounted, onUnmounted, computed, readonly } from "vue";
	import {
		useFloating,
		autoUpdate,
		offset,
		flip,
		shift,
		arrow,
		type Placement,
	} from "@floating-ui/vue";

	interface Props {
		placement?: Placement;
		offsetDistance?: number;
		showArrow?: boolean;
		trigger?: "click" | "hover" | "manual";
		disabled?: boolean;
		tooltipClass?: string;
		arrowClass?: string;
	}

	const props = withDefaults(defineProps<Props>(), {
		placement: "top",
		offsetDistance: 8,
		showArrow: true,
		trigger: "click",
		disabled: false,
		tooltipClass:
			"bg-grey-800 text-white text-xs rounded-lg shadow-xl px-3 py-2 z-50 pointer-events-none",
		arrowClass: "absolute w-3 h-3 bg-grey-800 rotate-45",
	});

	const emit = defineEmits<{
		(e: "show"): void;
		(e: "hide"): void;
	}>();

	const isOpen = ref(false);
	const reference = ref<HTMLElement>();
	const floating = ref<HTMLDivElement>();
	const arrowRef = ref<HTMLDivElement>();

	const { floatingStyles, middlewareData } = useFloating(
		reference,
		floating,
		{
			open: isOpen,
			placement: props.placement,
			whileElementsMounted: autoUpdate,
			middleware: [
				offset(props.offsetDistance),
				flip(),
				shift({ padding: 8 }),
				...(props.showArrow
					? [arrow({ element: arrowRef, padding: 8 })]
					: []),
			],
		},
	);

	const arrowStyles = computed(() => {
		if (!props.showArrow || !middlewareData.value.arrow) return {};

		const { x, y } = middlewareData.value.arrow;
		return {
			left: x != null ? `${x}px` : "",
			top: y != null ? `${y}px` : "",
		};
	});

	function show() {
		if (props.disabled) return;
		isOpen.value = true;
		emit("show");
	}

	function hide() {
		isOpen.value = false;
		emit("hide");
	}

	function toggle() {
		if (isOpen.value) {
			hide();
		} else {
			show();
		}
	}

	function handleClickOutside(event: MouseEvent) {
		if (
			props.trigger === "click" &&
			isOpen.value &&
			reference.value &&
			floating.value &&
			!reference.value.contains(event.target as Node) &&
			!floating.value.contains(event.target as Node)
		) {
			hide();
		}
	}

	function handleReferenceClick(event: Event) {
		if (props.trigger === "click") {
			event.stopPropagation();
			toggle();
		}
	}

	function handleReferenceMouseEnter() {
		if (props.trigger === "hover") {
			show();
		}
	}

	function handleReferenceMouseLeave() {
		if (props.trigger === "hover") {
			hide();
		}
	}

	onMounted(() => {
		if (props.trigger === "click") {
			document.addEventListener("click", handleClickOutside);
		}
	});

	onUnmounted(() => {
		if (props.trigger === "click") {
			document.removeEventListener("click", handleClickOutside);
		}
	});

	defineExpose({
		show,
		hide,
		toggle,
		isOpen: readonly(isOpen),
	});
</script>

<template>
	<div class="relative inline-block">
		<!-- Reference element (trigger) -->
		<div
			ref="reference"
			@click="handleReferenceClick"
			@mouseenter="handleReferenceMouseEnter"
			@mouseleave="handleReferenceMouseLeave"
		>
			<slot
				name="trigger"
				:isOpen="isOpen"
				:show="show"
				:hide="hide"
				:toggle="toggle"
			/>
		</div>

		<!-- Floating tooltip -->
		<Teleport to="body">
			<div
				v-if="isOpen"
				ref="floating"
				:style="floatingStyles"
				:class="tooltipClass"
			>
				<slot
					:hide="hide"
					:isOpen="isOpen"
				/>

				<!-- Arrow -->
				<div
					v-if="showArrow"
					ref="arrowRef"
					:class="arrowClass"
					:style="arrowStyles"
				></div>
			</div>
		</Teleport>
	</div>
</template>
